import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { href: "#home", label: "Home" },
  { href: "#problems", label: "Problem" },
  { href: "#see-concept", label: "See Concept First" },
  { href: "#process", label: "Process" },
  { href: "#pricing", label: "Pricing" },
  { href: "#care-plan", label: "Care Plan" },
  { href: "/social", label: "Social", route: true },
  { href: "#faq", label: "FAQ" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0B0F19]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 md:h-24 py-3 flex items-center justify-between">
        <a href="#home" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="relative inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-white shadow-[0_8px_30px_-8px_rgba(59,130,246,0.7)] overflow-hidden">
            <img
              src="/logo-icon.png"
              alt=""
              className="h-9 w-9 sm:h-10 sm:w-10 object-contain"
              draggable="false"
            />
          </span>
          <span className="font-display font-extrabold text-white text-lg sm:text-xl tracking-tight">
            Instant Site<span className="text-yellow-400"> Revival</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.href}
                to={n.href}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-cyan-300 hover:text-cyan-200 transition"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-slate-200/80 hover:text-white transition"
              >
                {n.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-claim-offer-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold px-5 py-2.5 text-sm shadow-[0_8px_30px_-6px_rgba(250,204,21,0.7)] transition-all hover:-translate-y-0.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
            </span>
            Claim Offer
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/10 border border-white/15 text-white"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden bg-[#0B0F19]/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-1">
            {NAV.map((n) =>
              n.route ? (
                <Link
                  key={n.href}
                  to={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 py-3 rounded-xl text-cyan-300 hover:bg-white/5"
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 py-3 rounded-xl text-slate-100 hover:bg-white/5"
                >
                  {n.label}
                </a>
              )
            )}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-claim-offer-btn"
              className="mt-2 inline-flex justify-center items-center rounded-full bg-yellow-400 text-slate-900 font-bold px-5 py-3"
            >
              Claim Offer
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
